import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useIntl } from '@grandvisionhq/state';
import { getWeekDaysForWeeksFromDate, isDateBefore, shiftDate, isSameDay } from '../../utils/datetime.js';
import AvailabilityCalendar from './AvailabilityCalendar.js';
import { useRangeByWeeks } from './hooks/use-range-by-weeks.js';
import { tileNameLabelsByWeeks, formatTileData, getLastWeekDay } from './utils.js';

const AvailabilityCalendarContainer = (_param)=>{
    var { selectedDate: _selectedDate , weekDate: _weekDate , weekCount =2 , closedDates , minEnabledDate: _minEnabledDate , maxEnabledDate , availability ={} , loading =false , noTimeSlots , onSelectDate , onChangeWeeks  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "selectedDate",
        "weekDate",
        "weekCount",
        "closedDates",
        "minEnabledDate",
        "maxEnabledDate",
        "availability",
        "loading",
        "noTimeSlots",
        "onSelectDate",
        "onChangeWeeks"
    ]);
    var ref1;
    const weekStartOffset = 1 // Currently, the weeks will start on monday for all opcos. In the future this may need configurability.
    ;
    const { getLabel  } = useIntl();
    const today = React.useRef(new Date());
    const [weekDate, setWeekDate] = React.useState(_weekDate !== null && _weekDate !== void 0 ? _weekDate : today.current);
    const [weeks, setWeeks] = React.useState(getWeekDaysForWeeksFromDate(weekDate, weekCount, weekStartOffset));
    const [selectedDate, setSelectedDate] = React.useState(_selectedDate);
    const minEnabledDate = _minEnabledDate !== null && _minEnabledDate !== void 0 ? _minEnabledDate : today.current;
    const labels = React.useMemo(()=>({
            pastButton: getLabel('appointments.component.availabilityCalendar.ariaLabels.pastButton'),
            futureButton: getLabel('appointments.component.availabilityCalendar.ariaLabels.futureButton'),
            legend: getLabel('appointments.component.availabilityCalendar.legend')
        })
    , [
        getLabel
    ]);
    const range = useRangeByWeeks(weeks);
    const tileNames = React.useMemo(()=>{
        var ref;
        return (ref = tileNameLabelsByWeeks(weeks)) === null || ref === void 0 ? void 0 : ref.map((label)=>getLabel(label)
        );
    }, [
        getLabel,
        weeks
    ]);
    const tiles = React.useMemo(()=>formatTileData(weeks, availability, closedDates, minEnabledDate, maxEnabledDate, selectedDate)
    , [
        weeks,
        availability,
        minEnabledDate,
        maxEnabledDate,
        selectedDate,
        closedDates
    ]);
    const firstWeekDay = (ref1 = weeks[0]) === null || ref1 === void 0 ? void 0 : ref1[0];
    const lastWeek = weeks[weeks.length - 1];
    const lastWeekDay = lastWeek === null || lastWeek === void 0 ? void 0 : lastWeek[lastWeek.length - 1];
    const showPastButton = firstWeekDay ? isDateBefore(minEnabledDate, shiftDate(firstWeekDay, 'day', -1)) : false // Default to `false` if `firstWeekDay` is undefined
    ;
    const showFutureButton = lastWeekDay && maxEnabledDate ? isDateBefore(shiftDate(lastWeekDay, 'day', 1), maxEnabledDate) : true // Default to `true` if `lastWeekDay` or `maxEnabledDate` is undefined
    ;
    const onClickPastButton = React.useCallback(()=>{
        const newWeekDate = shiftDate(weekDate !== null && weekDate !== void 0 ? weekDate : today.current, 'week', -weekCount);
        setWeekDate(newWeekDate);
        const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
        setWeeks(newWeeks);
        onChangeWeeks && onChangeWeeks(newWeeks);
    }, [
        weekDate,
        setWeekDate,
        weekCount,
        weekStartOffset,
        onChangeWeeks
    ]);
    const onClickFutureButton = React.useCallback(()=>{
        const newWeekDate = shiftDate(weekDate !== null && weekDate !== void 0 ? weekDate : today.current, 'week', weekCount);
        setWeekDate(newWeekDate);
        const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
        setWeeks(newWeeks);
        onChangeWeeks && onChangeWeeks(newWeeks);
    }, [
        weekDate,
        setWeekDate,
        weekCount,
        weekStartOffset,
        onChangeWeeks
    ]);
    const onClickTile = React.useCallback((date)=>{
        setSelectedDate(date);
        onSelectDate && onSelectDate(date);
    }, [
        onSelectDate
    ]);
    // Prop values changed
    React.useEffect(()=>setSelectedDate(_selectedDate)
    , [
        _selectedDate
    ]);
    React.useEffect(()=>{
        var ref, ref2;
        const newWeekDate = _weekDate !== null && _weekDate !== void 0 ? _weekDate : today.current;
        setWeekDate(_weekDate !== null && _weekDate !== void 0 ? _weekDate : today.current);
        const newWeeks = getWeekDaysForWeeksFromDate(newWeekDate, weekCount, weekStartOffset);
        setWeeks(newWeeks);
        const currFirstDay = (ref = weeks[0]) === null || ref === void 0 ? void 0 : ref[0];
        const currLastDay = getLastWeekDay(weeks);
        const newFirstDay = (ref2 = newWeeks[0]) === null || ref2 === void 0 ? void 0 : ref2[0];
        const newLastDay = getLastWeekDay(newWeeks);
        if (currFirstDay && currLastDay && newFirstDay && newLastDay) {
            if (isSameDay(currFirstDay, newFirstDay) && isSameDay(currLastDay, newLastDay)) {
                return;
            }
        } else {
            console.error('Missing required data for weeks or newWeeks');
        }
        onChangeWeeks && onChangeWeeks(newWeeks);
    // Run only when _weekDate changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        _weekDate,
        weekCount
    ]);
    // Run at first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(()=>onChangeWeeks && onChangeWeeks(weeks)
    , []);
    return /*#__PURE__*/ React.createElement(AvailabilityCalendar, swcHelpers.extends({}, rest, {
        labels: labels,
        range: range,
        showPastButton: showPastButton,
        showFutureButton: showFutureButton,
        onClickPastButton: onClickPastButton,
        onClickFutureButton: onClickFutureButton,
        onClickTile: onClickTile,
        tileNames: tileNames,
        tiles: tiles,
        loading: loading,
        noTimeSlots: noTimeSlots
    }));
};

export { AvailabilityCalendarContainer as default };
//# sourceMappingURL=AvailabilityCalendarContainer.js.map
